import React from "react";
import CitationTooltip, { Citation } from "../citation-tooltip/citation-tooltip";

export interface CitationMetadata {
  chunks: Citation[];
  supports: Array<{
    segment: {
      text: string;
      startIndex: number;
      endIndex: number;
    };
    groundingChunkIndices: number[];
  }>;
}

/**
 * Processes content and citations to insert citation markers
 */
export function processContentWithCitations(
  content: string,
  citations?: CitationMetadata
): {
  modifiedContent: string;
  citationMap: Map<string, { sources: Citation[]; number: number }>;
} {
  if (!citations || !citations.supports || citations.supports.length === 0) {
    return { modifiedContent: content, citationMap: new Map() };
  }

  // Pre-process: merge empty-text supports into the preceding non-empty support so
  // their chunk indices aren't lost and don't silently consume citation numbers.
  type MergedSupport = {
    segment: { text: string; startIndex: number; endIndex: number };
    groundingChunkIndices: number[];
  };
  const mergedSupports: MergedSupport[] = [];

  citations.supports.forEach((support) => {
    const segmentText = support.segment.text.trim();
    if (segmentText === "") {
      if (mergedSupports.length > 0) {
        const prev = mergedSupports[mergedSupports.length - 1];
        support.groundingChunkIndices.forEach((idx) => {
          if (!prev.groundingChunkIndices.includes(idx)) {
            prev.groundingChunkIndices.push(idx);
          }
        });
      }
    } else {
      mergedSupports.push({
        ...support,
        groundingChunkIndices: [...support.groundingChunkIndices],
      });
    }
  });

  const citationMap = new Map<string, { sources: Citation[]; number: number }>();
  const textToCitation = new Map<string, string>();
  let citationCounter = 1;

  mergedSupports.forEach((support) => {
    const citationSources = support.groundingChunkIndices
      .map((idx) => citations.chunks[idx])
      .filter(Boolean);

    if (citationSources.length > 0) {
      const marker = `[${citationCounter}]`;
      const segmentText = support.segment.text.trim();

      citationMap.set(marker, {
        sources: citationSources,
        number: citationCounter,
      });
      textToCitation.set(segmentText, marker);
      citationCounter++;
    }
  });

  // Replace cited text segments with text + marker
  let modifiedContent = content;
  textToCitation.forEach((marker, segmentText) => {
    const index = modifiedContent.indexOf(segmentText);
    if (index !== -1) {
      const before = modifiedContent.substring(0, index + segmentText.length);
      const after = modifiedContent.substring(index + segmentText.length);
      modifiedContent = before + marker + after;
    }
  });

  return { modifiedContent, citationMap };
}

/**
 * Component that wraps text content and renders citation tooltips for markers
 */
export const TextWithCitations: React.FC<{
  children: React.ReactNode;
  citationMap: Map<string, { sources: Citation[]; number: number }>;
}> = ({ children, citationMap }) => {
  // Handle array of children
  if (Array.isArray(children)) {
    return (
      <>
        {children.map((child, idx) => (
          <TextWithCitations key={idx} citationMap={citationMap}>
            {child}
          </TextWithCitations>
        ))}
      </>
    );
  }

  // Handle non-string children (like other React elements)
  if (typeof children !== "string") {
    return <>{children}</>;
  }

  // Split string by citation markers [1], [2], etc.
  const parts = children.split(/(\[\d+\])/);

  return (
    <>
      {parts.map((part, idx) => {
        const citationInfo = citationMap.get(part);
        if (citationInfo) {
          return (
            <CitationTooltip
              key={`cite-${idx}`}
              citations={citationInfo.sources}
              citationNumber={citationInfo.number}
            />
          );
        }
        return <React.Fragment key={idx}>{part}</React.Fragment>;
      })}
    </>
  );
};
